/**
 * DeviationSignalsBanner — advisory governance-deviation notice on the project Overview.
 *
 * Renders an amber alert listing the missing governance items reported by the most recent
 * unacknowledged deviation signal. This is advisory only — it never blocks any action and carries
 * no "action required" affordance. When there are no unacknowledged signals with missing items,
 * the component renders nothing.
 */
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { useDeviationSignals, type DeviationSignal } from '@/hooks/useDeviationSignals';

/** medium/high → amber "warning" badge; low → subdued "neutral" badge. */
function severityBadgeVariant(severity: DeviationSignal['severity']): BadgeVariant {
  return severity === 'low' ? 'neutral' : 'warning';
}

export interface DeviationSignalsBannerProps {
  projectId: string;
}

export function DeviationSignalsBanner({ projectId }: DeviationSignalsBannerProps): JSX.Element | null {
  const { data } = useDeviationSignals(projectId);

  // Only surface unacknowledged signals that actually report missing items, newest first.
  const actionable = (data ?? [])
    .filter((s) => !s.is_acknowledged && s.missing_items.length > 0)
    .sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());

  if (actionable.length === 0) return null;

  const latest = actionable[0];

  return (
    <Alert className="border-warning/40 bg-badge-yellow-bg text-text-primary">
      <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
      <AlertTitle className="flex flex-wrap items-center gap-2">
        Governance deviation detected
        {latest.phase && (
          <Badge variant="neutral" dot={false}>
            {latest.phase}
          </Badge>
        )}
        <Badge variant={severityBadgeVariant(latest.severity)} dot={false}>
          {latest.severity} severity
        </Badge>
      </AlertTitle>
      <AlertDescription className="mt-1 space-y-2">
        <p className="text-sm text-text-secondary">
          The following governance items appear to be missing for this phase. This is advisory only.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-text-primary">
          {latest.missing_items.map((item, i) => (
            <li key={`${latest.id}-${i}`}>{item}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

export default DeviationSignalsBanner;
